import React from "react";
import Layout from "@/components/layout/Layout";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState, useCallback } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AdminErrorBoundary } from "./AdminErrorBoundary";

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkAdminStatus = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) throw sessionError;
      
      if (!session) {
        // console.log("No session found, redirecting to login");
        navigate('/auth/login?redirectTo=/admin/tools');
        return;
      }

      // console.log("Checking admin status for:", session.user.id);
      
      const { data, error: adminError } = await supabase.rpc(
        'is_admin',
        { uid: session.user.id }
      );
      
      if (adminError) {
        // console.error("Error checking admin status:", adminError);
        throw new Error("Failed to verify admin permissions. Please try again.");
      }
      
      // console.log('Admin Check Result:', { userId: session.user.id, isAdmin: data });

      setIsAdmin(data);
      
      if (!data) {
        // console.log("User is not an admin, redirecting to home");
        toast.error("You do not have admin access");
        navigate('/');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "An error occurred while checking admin permissions";
      // console.error("Error in checkAdminStatus:", error);
      setError(errorMessage);
      toast.error("Failed to verify admin status");
      setIsAdmin(false);
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);

  if (isLoading) {
    return <Layout>
      <div className="container py-16">
        <div className="flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-grandpal-green" />
          <span className="ml-2">Verifying admin access...</span>
        </div>
      </div>
    </Layout>;
  }

  if (error) {
    return <Layout>
      <div className="container py-16">
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <span>Error: {error}</span>
          </div>
          <button 
            onClick={checkAdminStatus} 
            className="px-4 py-2 bg-grandpal-green text-white rounded-md hover:bg-grandpal-green/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    </Layout>;
  }

  if (isAdmin === false) {
    return <Layout>
      <div className="container py-16">
        <div className="flex items-center justify-center gap-2 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <span>You don't have permission to access this page</span>
        </div>
      </div>
    </Layout>;
  }

  return (
    <Layout hideFooter>
      <AdminErrorBoundary>
        {children}
      </AdminErrorBoundary>
    </Layout>
  );
}
